import * as THREE from 'three'
import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'
import type { WorldBox } from './modelBox'
import type {
  SectionLocalExtents,
  SectionOrientation,
} from '@viewer/store/sectionSlice'
import { viewBasis, type View2DDef } from './lib'

// ---------------------------------------------------------------------------
// "Ver no 3D": leva a câmara e a caixa de corte para a vista 2D.
//
// PORQUE NÃO SE USA O `OBC.Views` (@thatopen/components 3.4.2):
//
//   - `OBC.View` TRAZ A SUA PRÓPRIA CÂMARA (`readonly camera:
//     OrthoPerspectiveCamera`). O nosso mundo é montado com
//     `world.camera = new OBC.SimpleCamera(components)` e essa
//     instância está amarrada a meio viewer: `model.useCamera(...)` no
//     load de cada FragmentsModel (frustum culling / LOD), o
//     Highlighter, o ViewCube, o overlay da caixa de corte, o pipeline
//     de medições e a captura de viewpoints. Trocar `world.camera` em
//     runtime obriga a re-registar tudo isso e a lidar com o
//     `useCamera` dos modelos já carregados.
//   - `View.open = true` faz o componente assumir o controlo do
//     viewport e dos planos de corte, o que colide directamente com o
//     `renderer.clippingPlanes` que o `useSectionBox` gere.
//   - O `ClipEdges` (@thatopen/components-front) desenha as arestas do
//     corte na CENA 3D. É bonito, mas não é o que falta aqui: o
//     desenho 2D já sai do nosso próprio pipeline geométrico, com
//     camadas e cores por categoria, e vai para SVG/PDF/DXF. O
//     ClipEdges não alimenta nenhum desses formatos.
//
// Custo/benefício mau: era refazer o arranque do viewer para ganhar uma
// câmara ortográfica. Fica o FALLBACK, que usa a maquinaria que já
// existe e é exacto:
//
//   1. A caixa de corte passa a ser a "fatia" da vista: eixo Z local =
//      `viewDir`, plano de corte em w = 0 e fundo em w = depth. Ou
//      seja, o 3D mostra exactamente o volume que o desenho 2D
//      desenha.
//   2. A câmara vai para a normal do plano, a olhar ao longo do
//      `viewDir`, enquadrando a janela da vista.
//
// Limitação assumida e reportada: a câmara continua PERSPECTIVA (a
// `SimpleCamera` não tem projecção ortográfica). Alinhada com o
// `viewDir` e afastada, a leitura é praticamente ortográfica, mas não é
// a mesma coisa que o desenho.
// ---------------------------------------------------------------------------

/** Distância mínima da câmara ao plano, em metros. */
const MIN_CAMERA_DISTANCE = 2

export interface GoTo3DResult {
  ok: boolean
  /** Motivo em PT-PT quando `ok` é falso. */
  reason?: string
}

/** Referencial ortonormal da caixa de corte alinhado com a vista:
 *  axisX = right do desenho, axisY = up do desenho, axisZ = viewDir. */
export function sectionOrientationForView(
  view: View2DDef,
): SectionOrientation {
  const basis = viewBasis(view)
  return {
    origin: [basis.origin[0], basis.origin[1], basis.origin[2]],
    axisX: [basis.right[0], basis.right[1], basis.right[2]],
    axisY: [basis.up[0], basis.up[1], basis.up[2]],
    axisZ: [basis.viewDir[0], basis.viewDir[1], basis.viewDir[2]],
  }
}

/** Extensões da caixa que correspondem à vista: a janela no plano
 *  (crop, ou a projecção do bbox dos modelos quando não há crop) e a
 *  profundidade entre 0 e `depth`. */
export function sectionExtentsForView(
  view: View2DDef,
  worldBox: WorldBox | null,
): SectionLocalExtents {
  const basis = viewBasis(view)
  let minX = view.crop?.minX
  let maxX = view.crop?.maxX
  let minY = view.crop?.minY
  let maxY = view.crop?.maxY
  if (minX == null || maxX == null || minY == null || maxY == null) {
    minX = Infinity
    maxX = -Infinity
    minY = Infinity
    maxY = -Infinity
    if (worldBox) {
      for (const cx of [worldBox.min[0], worldBox.max[0]]) {
        for (const cy of [worldBox.min[1], worldBox.max[1]]) {
          for (const cz of [worldBox.min[2], worldBox.max[2]]) {
            const dx = cx - basis.origin[0]
            const dy = cy - basis.origin[1]
            const dz = cz - basis.origin[2]
            const x =
              dx * basis.right[0] + dy * basis.right[1] + dz * basis.right[2]
            const y = dx * basis.up[0] + dy * basis.up[1] + dz * basis.up[2]
            if (x < minX) minX = x
            if (x > maxX) maxX = x
            if (y < minY) minY = y
            if (y > maxY) maxY = y
          }
        }
      }
    }
    if (!Number.isFinite(minX) || minX >= maxX) {
      minX = -50
      maxX = 50
    }
    if (!Number.isFinite(minY) || minY >= maxY) {
      minY = -50
      maxY = 50
    }
  }
  return {
    minX,
    maxX,
    minY,
    maxY,
    minZ: 0,
    maxZ: Math.max(basis.depth, 1e-3),
  }
}

/** Aplica a vista ao 3D: caixa de corte na fatia da vista e câmara
 *  alinhada com o `viewDir`. Não lança: devolve `ok: false` com motivo
 *  quando o mundo ainda não está montado. */
export async function goToView3D(
  view: View2DDef,
  worldBox: WorldBox | null = null,
): Promise<GoTo3DResult> {
  const world = viewerApi.getWorld()
  if (!world) return { ok: false, reason: 'O viewer 3D ainda não está pronto.' }

  const basis = viewBasis(view)
  const orientation = sectionOrientationForView(view)
  const extents = sectionExtentsForView(view, worldBox)

  const store = useAppStore.getState()
  store.setSectionOrientation(orientation)
  store.setSectionExtents(extents)
  // Margem generosa no range para as pegas do overlay continuarem a
  // poder puxar a caixa para fora da fatia.
  const pad = Math.max(
    10,
    (extents.maxX - extents.minX) * 0.5,
    (extents.maxY - extents.minY) * 0.5,
  )
  store.setSectionExtentRange({
    minX: extents.minX - pad,
    maxX: extents.maxX + pad,
    minY: extents.minY - pad,
    maxY: extents.maxY + pad,
    minZ: extents.minZ - pad,
    maxZ: extents.maxZ + pad,
  })
  store.setSectionEnabled(true)

  // Enquadramento: metade da diagonal da janela dá uma distância que
  // mostra a vista inteira num FOV corrente (~50°).
  const halfW = Math.abs(extents.maxX - extents.minX) / 2
  const halfH = Math.abs(extents.maxY - extents.minY) / 2
  const distance = Math.max(
    MIN_CAMERA_DISTANCE,
    Math.hypot(halfW, halfH) * 1.6 + basis.depth,
  )
  const centreLocalX = (extents.minX + extents.maxX) / 2
  const centreLocalY = (extents.minY + extents.maxY) / 2
  const target = new THREE.Vector3(
    basis.origin[0] +
      basis.right[0] * centreLocalX +
      basis.up[0] * centreLocalY +
      basis.viewDir[0] * (basis.depth / 2),
    basis.origin[1] +
      basis.right[1] * centreLocalX +
      basis.up[1] * centreLocalY +
      basis.viewDir[1] * (basis.depth / 2),
    basis.origin[2] +
      basis.right[2] * centreLocalX +
      basis.up[2] * centreLocalY +
      basis.viewDir[2] * (basis.depth / 2),
  )
  const eye = new THREE.Vector3(
    target.x - basis.viewDir[0] * distance,
    target.y - basis.viewDir[1] * distance,
    target.z - basis.viewDir[2] * distance,
  )
  try {
    await world.camera.controls.setLookAt(
      eye.x,
      eye.y,
      eye.z,
      target.x,
      target.y,
      target.z,
      true,
    )
  } catch {
    return {
      ok: false,
      reason: 'Não foi possível mover a câmara para a vista.',
    }
  }
  viewerApi._invalidate()
  return { ok: true }
}
